import { Permission, ProductType, ProductTypePath, Role } from '@/generated/enums'
import { PATH_TO_PRODUCT_TYPE } from './reports/report_utils'
import { useAtomValue } from 'jotai'
import { userPermissionsAtom, userRoleAtom } from '@/atoms/permissions'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { ALL_MODULES_PATH } from '@/utils/constants'
import { TEST_ORG_ID } from '@/utils/constants'

const PAGES_ALLOWING_ALL_USERS: ProductTypePath[] = [
  ALL_MODULES_PATH as ProductTypePath,
  ProductTypePath.THREATS,
  ProductTypePath.PII_REMOVAL, // TODO DOP-8373: change path of PII to PII removal
  ProductTypePath.CREDENTIAL_LEAKS,
  ProductTypePath.MENTIONS,
  ProductTypePath.PHYSICAL_THREATS,
]

const ASSIGNABLE_ROLES = [Role.CUSTOMER_ADMIN, Role.CUSTOMER_USER, Role.CUSTOMER_VIEWER]

// Should replace current isEmployee at some point
export const isEmployee = (userRole: Role): boolean => userRole === Role.EMPLOYEE

export const isCustomerSuperAdmin = (userRole: Role): boolean =>
  userRole === Role.CUSTOMER_SUPER_ADMIN

export const assignableRoles = (userRole: Role, organizationId: string): Role[] => {
  if (isEmployee(userRole)) {
    return organizationId === TEST_ORG_ID
      ? [Role.EMPLOYEE, Role.CUSTOMER_SUPER_ADMIN, ...ASSIGNABLE_ROLES]
      : [Role.CUSTOMER_SUPER_ADMIN, ...ASSIGNABLE_ROLES]
  }
  return ASSIGNABLE_ROLES
}

export const isUserAuthorizedForProductType = (
  productType: ProductType,
  userProducts: ProductType[],
  isRbacEnabled: boolean,
): boolean => {
  if (isRbacEnabled && userProducts != null) {
    return userProducts.includes(productType)
  }
  return true
}

export const isUserAuthorizedForPage = (
  pathname: string,
  userProducts: ProductType[],
  isRbacEnabled: boolean,
): boolean => {
  const formattedPathname = pathname.replace(/^\/+|\/+$/g, '') as ProductTypePath
  if (
    isRbacEnabled &&
    userProducts !== null &&
    Object.values(ProductTypePath).includes(formattedPathname)
  ) {
    return (
      userProducts.includes(PATH_TO_PRODUCT_TYPE[formattedPathname]) ||
      PAGES_ALLOWING_ALL_USERS.includes(formattedPathname)
    )
  }
  return true
}

export const useIsUserAuthorizedForAction = (permission: Permission) => {
  const isRbacRoleRestrictEnabled = useFeatureFlagEnabled('rbac-role-restrict')
  const userPermissions = useAtomValue(userPermissionsAtom)

  if (
    !isRbacRoleRestrictEnabled &&
    isRbacRoleRestrictEnabled !== undefined // posthog taking a while to load here
  )
    return true

  return userPermissions?.has(permission)
}

export const useUserAuthorizedTagActions = () => {
  const isRbacRoleRestrictEnabled = useFeatureFlagEnabled('rbac-role-restrict')
  const userPermissions = useAtomValue(userPermissionsAtom)

  if (!isRbacRoleRestrictEnabled)
    return { canCreateTag: true, canUpdateTag: true, canDeleteTag: true }

  return {
    canCreateTag: userPermissions?.has(Permission.TAG_CREATE),
    canUpdateTag: userPermissions?.has(Permission.TAG_UPDATE),
    canDeleteTag: userPermissions?.has(Permission.TAG_DELETE),
  }
}

export const useUserAuthorizedUserActions = () => {
  const userPermissions = useAtomValue(userPermissionsAtom)

  return {
    canCreateUser: userPermissions?.has(Permission.USER_CREATE),
    canUpdateUser: userPermissions?.has(Permission.USER_UPDATE),
    canDeleteUser: userPermissions?.has(Permission.USER_DELETE),
  }
}

export const useUserAuthorizedPiiActions = () => {
  const userPermissions = useAtomValue(userPermissionsAtom)

  return {
    canReadAllPii: userPermissions?.has(Permission.PII_READ),
    canCreatePii: userPermissions?.has(Permission.PII_CREATE),
    canEditPii: userPermissions?.has(Permission.PII_EDIT),
    canDeletePii: userPermissions?.has(Permission.PII_DELETE),
  }
}

export const useUserRole = (): Role => {
  const userRole = useAtomValue(userRoleAtom)
  return userRole?.name as Role
}
